import { type Folder } from '@efdigital/ll-addon-data';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

import { FolderTitle } from './folder-title.gts';

export type Breadcrumb = {
  id: string;
  name: string;
  isRoot: boolean;
  isActive: boolean;
  depth: number;
};

interface FolderBreadcrumbSignature {
  Element: HTMLDivElement;
  Args: {
    folderAndContents: Folder;
    startFrom: string;
  };
  Blocks: {
    breadcrumb: [crumb: Breadcrumb];
  };
}

interface FolderBreadcrumbItemSignature {
  Args: {
    crumb: Breadcrumb;
    onClick: (crumb: Breadcrumb) => void;
  };
  Element: HTMLButtonElement;
}

export class FolderBreadcrumbItem extends Component<FolderBreadcrumbItemSignature> {
  @service declare store: Store;

  <template>
    <button
      class="btn btn-link p-0"
      type="button"
      {{on "click" (fn @onClick @crumb)}}
    ><FolderTitle
        @folderId={{@crumb.id}}
        as |title|
      >{{title}}</FolderTitle></button>
  </template>
}

export default class FolderBreadcrumb extends Component<FolderBreadcrumbSignature> {
  breadcrumb = (folderId: string | null): Breadcrumb[] => {
    if (!folderId) {
      return [] as Breadcrumb[];
    }

    const rootCrumb = {
      id: '|',
      name: 'Files',
      isRoot: true,
      isActive: false,
      depth: 0,
    };

    const crumbs =
      folderId.length <= 1
        ? [rootCrumb]
        : folderId
            .substring(1)
            .split('|')
            .reduce(
              ({ crumbs, currentPath }, name) => {
                const thisPath = currentPath + '|' + name;
                const crumb = {
                  id: thisPath,
                  name: name,
                  isRoot: false,
                  isActive: false,
                  depth: crumbs.length,
                };
                return {
                  crumbs: [...crumbs, crumb],
                  currentPath: thisPath,
                };
              },
              {
                crumbs: [rootCrumb],
                currentPath: '',
              }
            ).crumbs;

    crumbs[crumbs.length - 1]!.isActive = true;

    const startFromIndex = crumbs.findIndex(
      (crumb) => crumb.id === this.args.startFrom
    );
    if (startFromIndex !== -1) {
      return crumbs.slice(startFromIndex);
    }
    return crumbs;
  };

  <template>
    <nav aria-label="breadcrumb" ...attributes>
      <ol class="breadcrumb">
        {{#each (this.breadcrumb @folderAndContents.id) as |crumb|}}
          <li class="breadcrumb-item">{{yield crumb to="breadcrumb"}}</li>
        {{/each}}
      </ol>
    </nav>
  </template>
}
